import React, { useEffect, useMemo } from 'react';
import { FormHelperText, Grid, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import FolioCheckbox from '@/components/FolioCheckbox';
import { useSearchInsightPolicyTransactionsInfiniteQuery } from '@/features/policies/policiesApi';
import { FILTER_TYPES } from '@/features/policies/data';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import {
	buildDebtorDefaults,
	hasPositiveBalance,
	isAssociatedToActiveDebtor,
} from '@/components/dialogs/CompleteDealDialog/createDebtorDefaults';

const CompleteDealCreateDebtor = ({
	createDebtor,
	setCreateDebtor,
	setDebtorDefaults,
	loading,
}) => {
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { clientRow, insight } = useObjectContext();

	const clientId = clientRow?.clientId;
	const insightClientId = clientRow?.insightId ?? insight?.Id;

	const skipOutstandingInvoices = !insightClientId || !userDetails;

	const {
		data: outstandingInvoiceData,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isLoading: isLoadingOutstandingInvoices,
		isError: isOutstandingInvoicesError,
	} = useSearchInsightPolicyTransactionsInfiniteQuery(
		{
			...(!userDetails?.canViewGroup && { authRepId: userDetails?.authorisedRepId }),
			clientId: insightClientId,
			filterBy: FILTER_TYPES.ShowOutstanding,
			displayOnlyPosted: true,
		},
		{
			skip: skipOutstandingInvoices,
		},
	);

	useEffect(() => {
		if (hasNextPage && !isFetching && !isOutstandingInvoicesError) {
			fetchNextPage();
		}
	}, [hasNextPage, isFetching, isOutstandingInvoicesError, fetchNextPage]);

	const outstandingInvoices = useMemo(
		() => outstandingInvoiceData?.pages?.flatMap((page) => page?.Results ?? []) ?? [],
		[outstandingInvoiceData],
	);

	const debtorDefaults = useMemo(
		() =>
			buildDebtorDefaults({
				transactions: outstandingInvoices,
				clientName: clientRow?.name,
				fallbackOwnerDocId: userDetails?.userId,
			}),
		[outstandingInvoices, clientRow?.name, userDetails?.userId],
	);

	const debtorInvoicesLoading = isLoadingOutstandingInvoices || isFetching || hasNextPage;
	const canCreateDebtor =
		Boolean(debtorDefaults) && !debtorInvoicesLoading && Boolean(clientId);
	const createDebtorDisabled = loading || !canCreateDebtor;

	useEffect(() => {
		setDebtorDefaults(canCreateDebtor ? debtorDefaults : null);
	}, [canCreateDebtor, debtorDefaults, setDebtorDefaults]);

	const createDebtorHelperText = useMemo(() => {
		if (!createDebtorDisabled) {
			return '';
		}
		if (!clientId) {
			return 'This client record is not fully loaded yet, so a debtor cannot be created.';
		}
		if (!insightClientId) {
			return 'This client has no Insight id, so invoices cannot be loaded.';
		}
		if (isOutstandingInvoicesError) {
			return 'Could not load outstanding invoices.';
		}
		if (debtorInvoicesLoading) {
			return 'Loading outstanding invoices...';
		}
		if (!debtorDefaults) {
			const hasPositiveBalanceInvoices = outstandingInvoices.some(hasPositiveBalance);
			const allOnActiveDebtor =
				hasPositiveBalanceInvoices &&
				outstandingInvoices.filter(hasPositiveBalance).every(isAssociatedToActiveDebtor);

			if (allOnActiveDebtor) {
				return 'All outstanding invoices for this client are already on an active debtor.';
			}

			return 'No posted invoices with a positive balance were found for this client.';
		}
		return '';
	}, [
		createDebtorDisabled,
		insightClientId,
		isOutstandingInvoicesError,
		debtorInvoicesLoading,
		debtorDefaults,
		outstandingInvoices,
		clientId,
	]);

	return (
		<Grid size={{ xs: 12, md: 6 }}>
			<Stack>
				<FolioCheckbox
					checked={createDebtor}
					disabled={createDebtorDisabled}
					onChange={(e) => setCreateDebtor(e.target.checked)}
					label='Create Debtor for client'
				/>
				{createDebtorHelperText && (
					<FormHelperText>{createDebtorHelperText}</FormHelperText>
				)}
			</Stack>
		</Grid>
	);
};

export default CompleteDealCreateDebtor;
